import { ReactNode } from 'react';

import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { ColorToken } from '../designSystem/colors/colors';

export type StepIconVariant = 'done' | 'current-previous' | 'current-next' | 'next';

export type StepIconProps = Readonly<{
  label: string;
  variant: StepIconVariant;
  /** When set, the circle + label become an interactive control that navigates to this step. */
  onClick?: () => void;
  id?: string;
}>;

/**
 * Checkpoint in a vertical step timeline: status circle + label, with a connector rail above the `next` variants and below the `done` and `current-previous` ones.
 */
export const StepIcon = ({ label, variant, onClick, id }: StepIconProps) => {
  const iconMap: Record<StepIconVariant, ReactNode> = {
    done: (
      <Box sx={completedIconSx}>
        <AppIcon iconName="Check" diameter="20px" color="Colors/Base/White" />
      </Box>
    ),
    'current-previous': (
      <Box sx={incompleteIconSx}>
        <AppIcon
          iconName="Circle"
          diameter="12px"
          color="Colors/Base/White"
          fill="Colors/Base/White"
        />
      </Box>
    ),
    'current-next': (
      <Box sx={incompleteIconSx}>
        <AppIcon
          iconName="Circle"
          diameter="12px"
          color="Colors/Base/White"
          fill="Colors/Base/White"
        />
      </Box>
    ),
    next: (
      <Box sx={incompleteNextStepIconSx}>
        <AppIcon iconName="Circle" diameter="12px" color="Gray/300" fill="Gray/300" />
      </Box>
    ),
  };

  const textColorMap: Record<StepIconVariant, ColorToken> = {
    done: 'Colors/Text/text-brand-secondary',
    'current-previous': 'Colors/Text/text-secondary',
    'current-next': 'Colors/Text/text-secondary',
    next: 'Colors/Text/text-secondary',
  };

  return (
    <>
      {(variant === 'next' || variant === 'current-next') && (
        <Box sx={iconLineSx(variant === 'current-next')} />
      )}
      <Box
        id={id}
        sx={onClick ? clickableStepIconContainerStyle : stepIconContainerStyle}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
      >
        <Box sx={iconSlotSx}>{iconMap[variant]}</Box>
        <Typography
          variant="text-md-semibold"
          color={({ backrs }) => backrs.colors[textColorMap[variant]]}
        >
          {label}
        </Typography>
      </Box>
      {(variant === 'done' || variant === 'current-previous') && (
        <Box sx={iconLineSx(variant === 'done')} />
      )}
    </>
  );
};

// Widest circle. The slot is fixed at this size so labels stay aligned across steps
// whatever the variant's diameter.
const RINGED_ICON_SIZE = '36px';

const iconSlotSx: SxProps<Theme> = {
  width: RINGED_ICON_SIZE,
  display: 'flex',
  justifyContent: 'center',
  flexShrink: 0,
};

const completedIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: backrs.colors['Brand/500'],
  width: '32px',
  height: '32px',
  borderRadius: backrs.radius['radius-full'],
  zIndex: 2,
});

// Rings use inset shadows rather than an outline so they stay inside the box — scrolling
// ancestors clip anything painted outside it.
const incompleteIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: RINGED_ICON_SIZE,
  height: RINGED_ICON_SIZE,
  backgroundColor: backrs.colors['Brand/500'],
  boxShadow: `inset 0 0 0 2px ${backrs.colors['Brand/500']}, inset 0 0 0 4px ${backrs.colors['Colors/Base/White']}`,
  borderRadius: '50%',
  zIndex: 2,
});

const incompleteNextStepIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: RINGED_ICON_SIZE,
  height: RINGED_ICON_SIZE,
  backgroundColor: backrs.colors['Gray/50'],
  boxShadow: `inset 0 0 0 2px ${backrs.colors['Gray/200']}`,
  borderRadius: '50%',
  zIndex: 2,
});

const iconLineSx =
  (completed: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '2px',
    height: '24px',
    backgroundColor: completed ? backrs.colors['Brand/500'] : backrs.colors['Gray/300'],
    marginLeft: backrs.spacing['spacing-xl'],
    zIndex: 1,
  });

const stepIconContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
});

const clickableStepIconContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xl'],
  // The row is the interactive target; keep it snug so only the circle + label are clickable.
  alignSelf: 'flex-start',
  cursor: 'pointer',
  transition: 'opacity 0.15s ease',
  '&:hover, &:focus-visible': {
    opacity: 0.5,
  },
  '&:focus-visible': {
    outline: `2px solid ${backrs.colors['Brand/500']}`,
    outlineOffset: '2px',
    borderRadius: backrs.radius['radius-sm'],
  },
});
